<script setup lang="ts">
	import { computed, ComputedRef, PropType, Ref, ref, watch } from "vue";
	import { useI18n } from "vue-i18n";
	const { t } = useI18n();

	// Composables
	import { usePrice } from "@/features/cx/usePrice";
	import { useRepairAnalysis } from "@/features/repair_analysis/useRepairAnalysis";

	// Components
	import DayRepairMaterialTable from "@/features/repair_analysis/components/DayRepairMaterialTable.vue";
	import XITTransferActionButton from "@/features/xit/components/XITTransferActionButton.vue";
	import { Chart } from "highcharts-vue";

	// Types & Interfaces
	import {
		IPlanRepairAnalysisDataProp,
		IPlanRepairAnalysisElement,
	} from "@/features/planning/components/tools/planRepairAnalysis.types";
	import { PSelectOption } from "@/ui/ui.types";
	import {
		IMaterialIO,
		IMaterialIOMinimal,
	} from "@/features/planning/usePlanCalculation.types";
	import { Options } from "highcharts";

	// UI
	import { PForm, PFormItem, PSelect } from "@/ui";

	const props = defineProps({
		data: {
			type: Array as PropType<IPlanRepairAnalysisDataProp[]>,
			required: true,
		},
		cxUuid: {
			type: String,
			required: false,
			default: undefined,
		},
		planetNaturalId: {
			type: String,
			required: false,
			default: undefined,
		},
	});

	const DAY_MIN: number = 0;
	const DAY_MAX: number = 180;

	// Local State
	const localData = computed(() => props.data);
	const localCxUuid = computed(() => props.cxUuid);
	const localPlanetNaturalId = computed(() => props.planetNaturalId);

	const selectionOptions: ComputedRef<PSelectOption[]> = computed(() =>
		localData.value.map((b, i) => {
			return { label: b.name, value: i };
		})
	);

	const selectedBuilding = ref(localData.value.length > 0 ? 0 : undefined);
	const selectedDay = ref(90);
	const repairAnalysisElements = ref<IPlanRepairAnalysisElement[]>([]);
	const dailyRepairMaterials: Ref<Record<number, IMaterialIO[]>> = ref({});
	const singleMat = ref<{ name: string; data: (number | undefined)[] }[]>([]);

	const { getPrice } = await usePrice(localCxUuid, localPlanetNaturalId);
	const { calculateDailyRepairMaterials, daySelectOptions } =
		await useRepairAnalysis(localCxUuid, localPlanetNaturalId);

	async function calculateRep() {
		const r: IPlanRepairAnalysisElement[] = [];

		if (selectedBuilding.value === undefined) {
			repairAnalysisElements.value = r;
			return;
		}

		const materials: IMaterialIOMinimal[] =
			localData.value[selectedBuilding.value].constructionMaterials;

		let previous = 0;

		for (let i = DAY_MIN; i <= DAY_MAX; i++) {
			const efficiency =
				0.33 + 0.67 / (1 + Math.exp((1789 / 25000) * (i - 100.87)));
			const dailyRevenue =
				efficiency *
				localData.value[selectedBuilding.value].dailyRevenue;
			previous += dailyRevenue;
			const dailyRevenue_norm = previous / (i + 1);

			const mat = materials.map((m) => ({
				ticker: m.ticker,
				amount:
					m.input -
					Math.floor((m.input * (180 - Math.min(180, i))) / 180),
			}));

			// Calculate repair cost asynchronously
			const rep = await mat.reduce(async (sumPromise, element) => {
				const sum = await sumPromise;
				const price = await getPrice(element.ticker, "BUY");
				return sum + element.amount * price;
			}, Promise.resolve(0));

			const repSum = rep / (i + 1);
			const profit = i === 0 ? 0 : dailyRevenue_norm - repSum;

			r.push({
				day: i,
				efficiency,
				dailyRevenue,
				dailyRevenue_integral: previous,
				dailyRevenue_norm,
				materials: mat,
				repair: repSum,
				dailyRepair: rep,
				profit,
			});
		}

		// Adjust first day's profit if there are at least two entries
		if (r.length >= 2) {
			r[0].profit = r[1].profit;
		}

		repairAnalysisElements.value = r;
	}

	const maxValue: ComputedRef<number> = computed(() => {
		return Math.max(...repairAnalysisElements.value.map((o) => o.profit));
	});

	const maxDay: ComputedRef<number> = computed(() => {
		return repairAnalysisElements.value.findIndex(
			(e) => e.profit === maxValue.value
		);
	});

	async function calculateSingleMat() {
		if (!repairAnalysisElements.value.length) {
			singleMat.value = [];
			return;
		}

		const mats = repairAnalysisElements.value[0].materials.map(
			(m) => m.ticker
		);

		const results = await Promise.all(
			mats.map(async (mat) => ({
				name: mat,
				data: await Promise.all(
					repairAnalysisElements.value.map(async (r) => {
						const material = r.materials.find(
							(e) => e.ticker === mat
						);
						if (!material) return undefined;
						const price = await getPrice(material.ticker, "BUY");
						return material.amount * price;
					})
				),
			}))
		);

		singleMat.value = results;
	}

	const chartOptions: ComputedRef<Options> = computed(() => {
		return {
			chart: {
				type: "line",
				height: "300px",
			},
			yAxis: {
				title: {
					text: t("plan.tools.repair_analysis_details.chart.profit_day"),
				},
				labels: {
					format: "${text}",
				},
			},
			xAxis: {
				title: {
					text: t("plan.tools.repair_analysis_details.chart.days_since_repair"),
				},
			},
			tooltip: {
				headerFormat: `${t("plan.tools.repair_analysis_details.chart.day_prefix")} <strong>{point.key}</strong>${t("plan.tools.repair_analysis_details.chart.day_suffix")}<br />`,
				pointFormat: "$ {y:.2f}",
			},
			series: [
				{
					name: t("plan.tools.repair_analysis_details.chart.repair_analysis"),
					data: repairAnalysisElements.value.map((r) => r.profit),
				},
				{
					name: t("plan.tools.repair_analysis_details.chart.last_optimal"),
					data: [{ x: maxDay.value, y: maxValue.value }],
					marker: {
						symbol: "diamond",
					},
					dataLabels: {
						enabled: true,
						format: `${t("plan.tools.repair_analysis_details.chart.day_prefix")} <strong>{x}</strong>${t("plan.tools.repair_analysis_details.chart.day_suffix")}<br />$ {y:.2f}`,
					},
				},
			],
			title: {
				text: "",
			},
		} as Options;
	});

	const costOptions: ComputedRef<Options> = computed(() => {
		const s = [
			{
				name: t("plan.tools.repair_analysis_details.chart.total_cost"),
				data: repairAnalysisElements.value.map((r) => r.dailyRepair),
			},
		].concat(singleMat.value as { name: string; data: number[] }[]);

		return {
			chart: {
				type: "line",
				height: "300px",
			},
			tooltip: {
				headerFormat:
					"<strong>{series.name}</strong><br />" + `${t("plan.tools.repair_analysis_details.chart.day_prefix")} <strong>{point.key}</strong>${t("plan.tools.repair_analysis_details.chart.day_suffix")}<br />`,
				pointFormat: "$ {y:.2f}",
			},
			yAxis: {
				title: {
					text: t("plan.tools.repair_analysis_details.chart.cost"),
				},
				labels: {
					format: "${text}",
				},
			},
			xAxis: {
				title: {
					text: t("plan.tools.repair_analysis_details.chart.days_since_repair"),
				},
			},
			series: s,
			title: {
				text: "",
			},
		} as Options;
	});

	const selectPlanTransferMaterials = computed(() => {
		if (
			!dailyRepairMaterials.value ||
			!dailyRepairMaterials.value[selectedDay.value]
		)
			return [];

		return dailyRepairMaterials.value[selectedDay.value].map((e) => ({
			ticker: e.ticker,
			value: e.input,
		}));
	});

	// Recalculate whenever selectedBuilding or localData changes
	watch(
		[selectedBuilding, localData],
		async () => {
			calculateRep();
			dailyRepairMaterials.value = await calculateDailyRepairMaterials(
				localData.value
			);
		},
		{
			deep: true,
			immediate: true,
		}
	);

	// calculate single material if repair materials change
	watch(repairAnalysisElements, calculateSingleMat, {
		deep: true,
		immediate: true,
	});
</script>

<template>
	<h2 class="pb-3 text-white/80 font-bold text-lg">{{ $t("plan.tools.repair_analysis_details.title") }}</h2>
	<div class="grid grid-cols-1 xl:grid-cols-[400px_auto] gap-3 gap-x-6">
		<div>
			<h2 class="font-bold pb-3">{{ $t("plan.tools.repair_analysis_details.plan_heading") }}</h2>

			<PForm>
				<PFormItem :label="$t('plan.tools.repair_analysis_details.select_day')">
					<div class="w-full flex flex-row justify-between">
						<PSelect
							v-model:value="selectedDay"
							:options="daySelectOptions"
							searchable
							class="w-1/2 max-w-[200px]" />

						<XITTransferActionButton
							:elements="selectPlanTransferMaterials" />
					</div>
				</PFormItem>
			</PForm>

			<div class="py-3">
				<DayRepairMaterialTable
					v-if="
						dailyRepairMaterials &&
						dailyRepairMaterials[selectedDay]
					"
					:materials="dailyRepairMaterials[selectedDay]" />
			</div>
		</div>
		<div>
			<h2 class="font-bold pb-3">{{ $t("plan.tools.repair_analysis_details.individual_building") }}</h2>
			<PForm>
				<PFormItem :label="$t('plan.tools.repair_analysis_details.select_building')">
					<PSelect
						v-model:value="selectedBuilding"
						:options="selectionOptions"
						class="w-1/2 max-w-[200px]" />
				</PFormItem>
			</PForm>

			<template v-if="selectionOptions.length > 0">
				<div class="flex flex-col">
					<div>
						<h2 class="font-bold py-3">{{ $t("plan.tools.repair_analysis_details.profit_curve") }}</h2>
						<chart
							v-if="repairAnalysisElements.length > 0"
							ref="chart"
							class="hc"
							:options="chartOptions" />
					</div>
					<div>
						<h2 class="font-bold pb-3">{{ $t("plan.tools.repair_analysis_details.cost_breakdown") }}</h2>
						<chart
							v-if="repairAnalysisElements.length > 0"
							ref="chart"
							class="hc"
							:options="costOptions" />
					</div>
				</div>
			</template>
		</div>
	</div>
</template>
